import { useState, useMemo, useCallback, useEffect } from "react";

import { US_STATES } from "@/shared/constants/constants";
import { extractAddressFromPlace } from "@/shared/utils/addressUtils";

import type { EssentialModule, FormField } from "@/shared/types/estateEssentials";
import type { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import type { EstateItem } from "@/shared/types/estateTypes";

import { extractAddressFieldInfo, splitFieldsBySection } from "../utils/formDisplayUtils";
import { isFieldHidden } from "../utils/resolveFieldOptions";
import { buildVehicleFieldMap, getVehiclePrefix } from "../utils/vehicleUtils";

interface UseGuideFormParams {
  open: boolean;
  module: EssentialModule;
  initialData?: Record<string, string>;
  onSubmit: (formData: Record<string, string>) => void;
  deceasedData?: DeceasedUserData;
  relationship?: string;
  vehicleItems: EstateItem[];
}

export function useGuideForm({
  open,
  module,
  initialData,
  onSubmit,
  deceasedData,
  relationship,
  vehicleItems,
}: UseGuideFormParams) {
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [initialAttentionKeys, setInitialAttentionKeys] = useState<Set<string>>(new Set());
  const [hasInitForCurrentOpen, setHasInitForCurrentOpen] = useState(false);

  const fields = module.formFields ?? [];

  const dependentMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const f of fields) {
      if (f.dependsOn) {
        const list = map.get(f.dependsOn) ?? [];
        list.push(f.key);
        map.set(f.dependsOn, list);
      }
    }
    return map;
  }, [fields]);

  const { reviewFields, editableFields } = useMemo(() => splitFieldsBySection(fields), [fields]);

  const hasSections = reviewFields.length > 0 && editableFields.length > 0;

  const {
    addressField,
    addrPrefix,
    nonAddressFields: nonAddressEditableFields,
  } = useMemo(() => extractAddressFieldInfo(editableFields), [editableFields]);

  const hasAddressField = !!addressField;

  const {
    addressField: reviewAddressField,
    addrPrefix: reviewAddrPrefix,
    nonAddressFields: nonAddressReviewFields,
  } = useMemo(() => extractAddressFieldInfo(reviewFields), [reviewFields]);

  const hasReviewAddressField = !!reviewAddressField;
  const deceasedFirstName = deceasedData?.displayName || "Deceased";

  // Reset init flag when modal closes
  useEffect(() => {
    if (!open) setHasInitForCurrentOpen(false);
  }, [open]);

  // Initialize form values when modal is open, data is available, and not yet initialized
  useEffect(() => {
    if (!open || hasInitForCurrentOpen || fields.length === 0) return;

    const hasData = initialData && Object.keys(initialData).length > 0;
    if (!hasData) return;

    const initial: Record<string, string> = {};
    for (const field of fields) {
      initial[field.key] = initialData[field.key] ?? "";
    }
    // Preserve extra keys not in fields (prefixed vehicle keys, selected_vehicle_id)
    for (const [key, value] of Object.entries(initialData)) {
      if (/^(v\d+_|new\d+_|est\d+_)/.test(key) || key === "selected_vehicle_id") {
        initial[key] = value;
      }
    }
    // Pre-populate est{id}_ keys from vehicle metadata for each estate vehicle
    if (vehicleItems.length > 0) {
      const transferKeys = fields.filter((f) => f.sectionGroup === "transfer_info");
      const selectedId = initial.selected_vehicle_id;
      for (const v of vehicleItems) {
        const prefix = `est${v.id}_`;
        const meta = (v.metadata ?? {}) as Record<string, unknown>;
        const isSelected = String(v.id) === selectedId;
        const fieldMap = buildVehicleFieldMap(meta, {
          userFullName: initial.user_full_name,
          relationship: relationship ?? "",
        });
        for (const f of transferKeys) {
          const prefixedKey = `${prefix}${f.key}`;
          if (!initial[prefixedKey]) {
            const metaVal = fieldMap[f.key] ?? "";
            const baseVal = isSelected ? (initial[f.key] ?? "") : "";
            initial[prefixedKey] = metaVal || baseVal;
          }
        }
      }
    }
    setFormValues(initial);
    setHasInitForCurrentOpen(true);

    const emptyRequired = new Set<string>();
    const emptyReviewRequired = new Set<string>();
    const isVehicle = vehicleItems.length > 0;
    for (const f of fields) {
      if (isFieldHidden(f, initial)) continue;
      if (!f.required) continue;
      if (isVehicle && f.sectionGroup === "transfer_info") continue;
      if (!initial[f.key]?.trim()) {
        emptyRequired.add(f.key);
        if (f.section === "review") emptyReviewRequired.add(f.key);
      }
    }
    setInitialAttentionKeys(emptyRequired);
    setIsReviewMode(emptyReviewRequired.size === 0);
  }, [open, hasInitForCurrentOpen, initialData, fields, vehicleItems]);

  const handleFieldChange = useCallback(
    (key: string, value: string) => {
      setFormValues((prev) => {
        const next = { ...prev, [key]: value };
        const deps = dependentMap.get(key);
        if (deps) {
          for (const dep of deps) {
            next[dep] = "";
          }
        }
        return next;
      });
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [dependentMap],
  );

  const handlePrefixedAddressSelect = useCallback(
    (
      prefix: string,
      value: string,
      placeDetails?: google.maps.places.Place | null,
      subpremise?: string | null,
    ) => {
      const extracted = extractAddressFromPlace(value, placeDetails, subpremise);
      const stateAbbr =
        US_STATES.find((s) => s.label === extracted.state)?.value || extracted.state;

      setFormValues((prev) => ({
        ...prev,
        [`${prefix}_address`]: extracted.streetAddress,
        [`${prefix}_apartment`]: extracted.apartment || prev[`${prefix}_apartment`] || "",
        [`${prefix}_city`]: extracted.city || prev[`${prefix}_city`] || "",
        [`${prefix}_state`]: stateAbbr || prev[`${prefix}_state`] || "",
        [`${prefix}_zip`]: extracted.zipCode || prev[`${prefix}_zip`] || "",
      }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const hasEstateVehicles = (vehicleItems?.length ?? 0) > 0;
    for (const field of fields) {
      if (isFieldHidden(field, formValues)) continue;
      if (hasEstateVehicles && field.sectionGroup === "transfer_info") continue;
      const val = formValues[field.key]?.trim();
      if (field.required && !val) {
        newErrors[field.key] = "Required";
      } else if (val && field.pattern && !field.pattern.test(val)) {
        newErrors[field.key] = field.patternMessage ?? "Invalid format";
      }
    }

    const transferFields = fields.filter(
      (f: FormField) => f.sectionGroup === "transfer_info" && f.required,
    );
    if (transferFields.length > 0) {
      const vehicleCount = parseInt(formValues.vehicle_count ?? "1", 10);
      const estateVehicleCount = vehicleItems?.length ?? 0;
      const selectedId = formValues.selected_vehicle_id;

      if (estateVehicleCount > 0 && selectedId) {
        const prefix = getVehiclePrefix(selectedId);
        for (const field of transferFields) {
          const key = `${prefix}${field.key}`;
          if (!formValues[key]?.trim()) newErrors[key] = "Required";
        }
      } else {
        for (let i = 1; i < vehicleCount; i++) {
          const prefix = `v${i}_`;
          for (const field of transferFields) {
            const key = `${prefix}${field.key}`;
            if (!formValues[key]?.trim()) newErrors[key] = "Required";
          }
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(formValues);
  }, [fields, formValues, onSubmit, vehicleItems]);

  const attentionKeys = initialAttentionKeys;
  const activeAttentionCount = useMemo(() => {
    let count = 0;
    for (const key of attentionKeys) {
      if (!formValues[key]?.trim()) count++;
    }
    return count;
  }, [attentionKeys, formValues]);

  return {
    formValues,
    setFormValues,
    errors,
    isReviewMode,
    attentionKeys,
    activeAttentionCount,
    fields,
    reviewFields,
    editableFields,
    hasSections,
    hasAddressField,
    addrPrefix,
    nonAddressEditableFields,
    hasReviewAddressField,
    reviewAddrPrefix,
    nonAddressReviewFields,
    deceasedFirstName,
    handleFieldChange,
    handlePrefixedAddressSelect,
    handleSubmit,
  };
}
